// Aktoren-Summary-Store: Schalter, Plugs, Lampen + Dimmer.
//
// Datenfluss (seit 03.11.2026 über das Homelab-Protokoll, docs/protokoll.md
// › „Aktoren über das Protokoll“): jedes Zigbee-Gerät des Aktoren-Caches
// (ZigbeeStateHandler) ist eine Instanz aktor.<id>.stand. Das Backend
// (internal/protokoll/aktoren) gleicht den Cache auf Anstoß (jedes z2m-Echo,
// jede Availability, jeder optimistische Sollwert der Engine, gebündelt 250 ms)
// und alle 60 s ab; der Hub verwirft Unverändertes und schickt Deltas. Bis dahin
// holte jeder Tab /v2-api/aktoren/summary alle 15 s und bei jedem WS-Signal
// "aktoren" (200 ms entprellt) die ganze Liste neu.
//   - Hier steht die einzige Abo-Stelle (`aktor.*.stand`). Aus einem Stand baut
//     lib/aktoren-protokoll.ts den Eintrag in genau der Form der REST-Antwort.
//   - REST (`GET /v2-api/aktoren/summary`) nur beim Start, als Revalidator
//     (Reconnect, Tab kommt zurück), in refreshAktorenSummary (nach einer
//     Aktion) und als Neuversuch alle 15 s, solange ein Abruf scheiterte. KEIN
//     Takt; den alten WS-Kanal "aktoren" gibt es seit 03.11.2026 nicht mehr.
//   - Stände und Abrufe mischen wie in agents-summary (ProtokollListe): Stände
//     in-place, ein Stand während eines Abrufs liegt über dessen Antwort, ein
//     `weg` hinterlässt einen Grabstein.
//
// Das Backend-Cache ist Live-Daten: nach einem Dienst-Neustart ist es leer und
// füllt sich mit dem nächsten z2m-Echo. SWR: ja -- die zuletzt gesehene Liste
// kommt beim Start aus der Ablage (Schlüssel "aktoren", Form wie die Antwort),
// gedrosselt geschrieben (höchstens alle 2 s), und überbrückt die Zeit bis dahin.

import { getJSON } from "./api";
import { vergleicheAktor, aktorAusProtokoll, aktorIdAusInstanz } from "./aktoren-protokoll";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import { ProtokollListe } from "./protokoll-liste.svelte";
import { abonniere } from "./protokoll/client.svelte";
import type { AktorStand } from "./protokoll/schema.gen";
import { skaliere } from "./protokoll/skaliere";
import { registerRevalidator } from "./revalidate.svelte";

export interface AktorDevice {
  name: string;
  kind: "switch" | "light" | "remote";
  state: string; // "ON" | "OFF" | "" (remotes have no state)
  brightness?: number | null;
  color_temp?: number | null;
  color_hex?: string | null;
  last_seen: string;
  // Reachability aus z2m-availability-Topic. undefined = unbekannt
  // (Feature aus / noch keine Nachricht), true = erreichbar,
  // false = z2m sieht das Geraet nicht.
  available?: boolean;
  // Battery in Prozent (0..100), nur fuer batteriebetriebene Remotes.
  battery?: number;
  // Zigbee-Funkqualitaet 0..255.
  linkquality?: number;
}

export interface AktorenSummary {
  total: number;
  devices: AktorDevice[];
}

const STORAGE_KEY = "aktoren";

const _liste = new ProtokollListe<AktorDevice>((e) => e.name, vergleicheAktor);
// Ob die Liste etwas weiß (Ablage, Abruf oder Stand): vorher `undefined`.
let _geladen = $state(false);
let _started = false;
let _unsub: Array<() => void> = [];
// Instanzname -> Name des Aktors: `weg` nennt nur die Instanz.
const _instanzen = new Map<string, string>();

// ── Persistenz ───────────────────────────────────────────────────────

// Aus dem Protokoll kommt oft etwas (jedes Echo einer Lampe): gespeichert wird
// gebündelt, höchstens alle 2 s.
let _persistTimer: ReturnType<typeof setTimeout> | undefined;
function persistSpaeter(): void {
  if (_persistTimer) return;
  _persistTimer = setTimeout(() => {
    _persistTimer = undefined;
    const devices = $state.snapshot(_liste.items);
    const g: AktorenSummary = { total: devices.length, devices };
    persist(STORAGE_KEY, g);
  }, 2_000);
}

// ── Stände ──────────────────────────────────────────────────────────

function beiStand(instanz: string, daten: Record<string, unknown>): void {
  if (aktorIdAusInstanz(instanz) === undefined) return;
  const st = skaliere<AktorStand>("aktor.stand", daten);
  if (!st.name) return;
  _instanzen.set(instanz, st.name);
  const geaendert = _liste.stand(aktorAusProtokoll(st));
  // Vor dem ersten Abruf legt laden() die Geräte über die Antwort; bis dahin
  // wäre die Liste leer, obwohl Stände da sind.
  if (_liste.bereit) _geladen = true;
  if (geaendert) persistSpaeter();
}

function beiWeg(instanz: string): void {
  const name = _instanzen.get(instanz);
  if (name === undefined) return;
  _instanzen.delete(instanz);
  if (_liste.weg(name)) persistSpaeter();
}

// ── REST ─────────────────────────────────────────────────────────────

// Nach einem gescheiterten Abruf ein neuer Versuch: ohne Takt käme sonst erst
// der nächste Revalidator -- und bis dahin bliebe ein aus der Ablage geholtes,
// inzwischen verschwundenes Gerät stehen (für das kommt kein `weg`, diese
// Sitzung kannte seine Instanz nie).
const NEUVERSUCH_MS = 15_000;
let _neuversuch: ReturnType<typeof setTimeout> | undefined;

// Lauf: zählt Start und Stopp hoch. Eine Antwort, die erst nach dem Stopp
// (oder nach einem neuen Start) ankommt, gehört zu keinem laufenden Store.
let _lauf = 0;

async function laden(): Promise<void> {
  const ab = _liste.nr;
  const lauf = _lauf;
  let rest: AktorDevice[] | undefined;
  try {
    rest = (await getJSON<AktorenSummary>("/v2-api/aktoren/summary")).devices ?? [];
  } catch {
    rest = undefined;
  }
  if (lauf !== _lauf) return;
  try {
    // Was das Protokoll während eines gescheiterten Abrufs gebracht hat, gilt
    // trotzdem -- beim ersten Abruf wäre die Liste sonst leer, bis jedes Gerät
    // sich ändert.
    if (_liste.mische(rest, ab)) persistSpaeter();
    if (rest || _liste.neuer(ab)) _geladen = true;
  } finally {
    _liste.bereit = true;
  }
  if (!rest && _started && !_neuversuch) {
    _neuversuch = setTimeout(() => {
      _neuversuch = undefined;
      if (_started) void laden();
    }, NEUVERSUCH_MS);
  }
}

export function startAktorenSummary(): void {
  if (_started) return;
  _started = true;
  _lauf++;
  // SWR: die gecachte Liste sofort einspielen, damit die Licht-Karte beim
  // Reload nicht "lade..." zeigt. Das Protokoll bringt die frischen Werte
  // danach in Sekunden, der Cache deckt nur die First-Paint-Lücke.
  const cached = loadPersisted<AktorenSummary>(STORAGE_KEY);
  if (cached) {
    _liste.items = cached.devices ?? [];
    _geladen = true;
  }
  void laden();
  _unsub.push(abonniere("aktor.*.stand", { rate: "normal" }, { stand: beiStand, weg: beiWeg }));
  // ⚠️ Der Protokoll-Kanal allein reicht NICHT. Reißt die Leitung ab -- und
  // jeder Deploy tut das --, sind verpasste Löschungen weg. registerRevalidator
  // holt beim Reconnect und beim Zurückkommen des Tabs alles frisch.
  _unsub.push(registerRevalidator(laden));
}

export function stopAktorenSummary(): void {
  _started = false;
  _lauf++;
  for (const off of _unsub) off();
  _unsub = [];
  if (_persistTimer) {
    clearTimeout(_persistTimer);
    _persistTimer = undefined;
  }
  if (_neuversuch) {
    clearTimeout(_neuversuch);
    _neuversuch = undefined;
  }
  _liste.leere();
  _instanzen.clear();
  _geladen = false;
}

// Die Liste im Format der REST-Antwort. Neu gebaut nur, wenn die Liste selbst
// sich ändert (Gerät kommt oder geht); eine Änderung an einem Gerät geschieht
// in-place und zieht nur die Leser dieses Geräts.
const _summary = $derived.by((): AktorenSummary | undefined =>
  _geladen ? { total: _liste.items.length, devices: _liste.items } : undefined,
);

export function getAktorenSummary(): AktorenSummary | undefined {
  return _summary;
}

// Manuell triggern -- z. B. nachdem ein Toggle/Set durch ist, damit der
// UI-State nicht erst auf das Protokoll warten muss (bis zu 250 ms).
export function refreshAktorenSummary(): Promise<void> {
  // Ohne Start (Seite verlassen, Aktion endet später) nichts mehr füllen.
  if (!_started) return Promise.resolve();
  return laden();
}
